import { HTMLAttributes, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import Image from '@/components/base/Image'
import Motion from '@/components/base/Motion'
import ScrollingIcon from '@/components/base/ScrollingIcon'
import Typography from '@/components/base/Typography'
import { useLenis } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { useLoaderStore } from '@/store/loader'
import { isTouch } from '@/utils/device'

import { heroResize } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPVideo } from '@/types'

export type HeroHomeProps = HTMLAttributes<HTMLElement> &
  Partial<{
    poster: {
      desktop: WPImage
      mobile: WPImage
      hasVideo: boolean
    }
    video: {
      desktop: WPVideo
      mobile: WPVideo
    }
    title: string
  }>

const HeroHome = ({ poster, video, title, className }: HeroHomeProps) => {
  const { strings } = useStore()
  const wrapper = useRef<HTMLDivElement>(null!)
  const loaded = useLoaderStore((state) => state.pageLoaded)
  const { lenis } = useLenis()

  useGSAP(
    () => {
      if (!lenis || !loaded) return
      const timeline = gsap.timeline()

      const triggerElement = document.querySelector('[data-motion-ghost]')
      let tlHeroAnimation: gsap.core.Timeline

      if (triggerElement) {
        tlHeroAnimation = gsap.timeline().fromTo(
          '[data-motion-media]',
          { scale: 1, y: 0 },
          {
            scale: 1.1,
            y: '-30%',
            scrollTrigger: {
              trigger: triggerElement,
              scrub: true,
              start: 'bottom bottom',
              end: 'bottom top'
            }
          }
        )
        timeline.add(tlHeroAnimation)
      }

      return () => {
        if (timeline) timeline.kill()
      }
    },
    {
      dependencies: [lenis, loaded],
      scope: wrapper,
      revertOnUpdate: true
    }
  )

  return (
    <>
      <Motion animation={heroResize}>
        {(motionRef) => (
          <section
            ref={(ref) => {
              // @ts-ignore
              motionRef.current = ref
              // @ts-ignore
              wrapper.current = ref
            }}
            data-hero
            className={cn([
              'fixed left-0 top-0 z-10 h-svh w-full text-white',
              className
            ])}
          >
            <div
              className="relative z-10 h-full w-full overflow-hidden"
              data-hero-section
            >
              <div
                className={cn([
                  'relative z-10 h-full w-full',
                  'after:absolute after:left-0 after:top-0 after:z-20 after:h-full after:w-full after:bg-black/25'
                ])}
                data-motion-media
              >
                {poster?.hasVideo ? (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute z-10 h-full w-full object-cover"
                  >
                    <source
                      src={isTouch ? video?.mobile.url : video?.desktop.url}
                      type={
                        isTouch
                          ? video?.mobile.mime_type
                          : video?.desktop.mime_type
                      }
                    />
                  </video>
                ) : (
                  <Image
                    source={isTouch ? poster?.mobile : poster?.desktop}
                    priority={true}
                    fill
                    fit="cover"
                    className="rounded-[3rem]"
                  />
                )}
              </div>
              <div className="absolute bottom-gutter left-gutter right-gutter z-10 flex flex-col gap-[4.8rem] md:block md:gap-0 lg:bottom-[6rem]">
                {title ? (
                  <Typography
                    className="mx-auto -mb-[1.2rem] text-center !leading-[1.2] md:w-[75%]"
                    variant="h1"
                    component="h1"
                    data-motion-title
                  >
                    {title}
                  </Typography>
                ) : null}
                <div className="flex items-end justify-between" data-motion-cta>
                  <ScrollingIcon
                    className="lg:absolute lg:bottom-0 lg:left-0"
                    theme="dark"
                  />
                  <Typography
                    variant="label"
                    className="lg:absolute lg:bottom-0 lg:right-0"
                  >
                    {strings?.bottom_right_string}
                  </Typography>
                </div>
              </div>
            </div>
          </section>
        )}
      </Motion>
      <div
        className="pointer-events-none relative z-10 h-lvh w-full bg-transparent"
        data-motion-ghost
      ></div>
    </>
  )
}

export default HeroHome
